import { useCallback, useEffect } from 'react';
import { plural, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import get from 'lodash-es/get';
import intersection from 'lodash-es/intersection';
import styled from 'styled-components';

import { CalendarDirection } from '@rover/components/src/datetime/DatePicker';
import ServiceTypeFilter, {
  ServiceFilterType,
} from '@rover/components/src/formFields/ServiceTypeFilter';
import { LOCATION_DEFAULT_TITLE_1 } from '@rover/constants/src/location/location.constants';
import {
  DROP_OFF_PICK_UP_SUPPORTED_SERVICES,
  EMPTY_LOCATION,
} from '@rover/constants/src/search/searchPage.constants';
import { Box, Heading, Text } from '@rover/kibble/core';
import Advanced from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Advanced';
import {
  DayTimeAvailability,
  DaytimeServiceScheduler,
  FulltimeAvailability,
  OvernightDateRange,
} from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Dates';
import Location from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Location';
import PriceFilter from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/PriceFilter';
import StarSitterFilter from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/StarSitterFilter';
import PetCounter from '@rover/react-lib/src/pages/search/SearchPage/components/PetCounter';
import {
  advancedFiltersCounter,
  getServiceType,
  hasAdvancedFilters,
  shouldShowControls,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import getMaxRequestDate from '@rover/react-lib/src/utils/getMaxRequestDate';
import type { ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200SettingsDogSizeBuckets';
import { useGateValue } from '@rover/rsdk/src/modules/Statsig';
import useAvailablePets from '@rover/shared/js/hooks/useAvailablePets';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useIsBHUserShim from '@rover/shared/js/hooks/useIsBHUserShim';
import Pets from '@rover/shared/js/search/components/Pets';
import PetSize from '@rover/shared/js/search/components/PetSize';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type { SearchDerivedData } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { Pet } from '@rover/types/src/Pet';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType, ServiceTypeOption } from '@rover/types/src/ServiceType';
import { ServiceTypeSlug } from '@rover/types/src/ServiceType';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';
import {
  getIsPriceTransparency,
  getIsPriceTransparencyFull,
} from '@rover/utilities/priceTransparency';

import { CollectionWrapper, FilterWrapper } from '../common';
import PriceToggle from '../PriceToggle';

export type Props = {
  currencyCode: string;
  countryCode: string;
  getPriceUnit: (arg0: string) => string;
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  hasPets: boolean;
  isCatOnly: boolean;
  language: string;
  onScrollableFilterChange: () => void;
  onUpdateInnerHeight: () => void;
  petOptions: Pet[];
  petSizeBuckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets;
  priceFilterMaxValue: number;
  priceFilterMinValue: number;
  searchFilters: SearchFilters;
  serviceTypeFilterOptions: SearchFiltersOptions;
  serviceTypeOptions: ServiceTypeOption[];
  updateFiltersAndFireSearchOnChange: (searchFilters: Partial<SearchFilters>) => void;
  weightUnit: string;
  queryDerivedData: SearchDerivedData;
  priceTransparencyLevel?: PriceTransparencyLevel;
  isShowingPriceInCurrency: boolean;
  toggleIsShowingPriceInCurrency: (toggle: boolean) => void;
  cardTreatment: SearchCardTreatment;
};

const FILTERS_TO_RESET_SCROLL: Array<keyof Partial<SearchFilters>> = [
  'serviceType',
  'location',
  'startDate',
  'endDate',
  'petType',
  'giantDogs',
  'largeDogs',
  'mediumDogs',
  'smallDogs',
  'starSitter',
  'inSittersHome',
];

const StyledCollectionWrapper = styled(CollectionWrapper)`
  padding: 10px 10px 0 10px;
`;

const AdvancedWrapper = styled(FilterWrapper)`
  background-color: ${({ theme }) => theme.background.secondary};
  margin-bottom: 0;
  margin-left: ${({ theme }) => theme.space['-4x']};
  margin-right: -15px;
  padding: ${({ theme }) => theme.space['4x']};
`;

const SidebarFiltersCollection = ({
  currencyCode,
  countryCode,
  getPriceUnit,
  getServiceTypeBySlug,
  hasPets,
  isCatOnly,
  language,
  onScrollableFilterChange,
  onUpdateInnerHeight,
  petOptions,
  petSizeBuckets,
  priceFilterMaxValue,
  priceFilterMinValue,
  queryDerivedData,
  searchFilters,
  serviceTypeFilterOptions,
  serviceTypeOptions,
  updateFiltersAndFireSearchOnChange,
  weightUnit,
  priceTransparencyLevel = PriceTransparencyLevels.none,
  isShowingPriceInCurrency,
  toggleIsShowingPriceInCurrency,
  cardTreatment,
}: Props): JSX.Element => {
  const { i18n } = useLingui();
  const brandAvailablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandAvailablePets);
  const isBHUser = useIsBHUserShim();
  const serviceType = getServiceTypeBySlug(searchFilters.serviceType);
  const isIncludingFees = serviceTypeFilterOptions.minprice?.fullprice === true;
  const { morningAvailability, middayAvailability, eveningAvailability } = serviceTypeFilterOptions;

  const { showStarSitterFilter } = queryDerivedData;
  const { showLowBookingFilter } = queryDerivedData;
  const { starSitterLink } = queryDerivedData;

  // Abstract this into a selector in https://roverdotcom.atlassian.net/browse/DEV-42261
  const startPlaceholder = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(searchFilters.serviceType)
    ? t`Drop off`
    : t`Start`;
  const endPlaceholder = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(searchFilters.serviceType)
    ? t`Pick up`
    : t`End`;

  const { isStaffOrImpersonating } = useCurrentUserShim();

  const handleChange = useCallback(
    (changedFilters: Partial<SearchFilters>): void => {
      const changedFiltersKeys = Object.keys(changedFilters);
      const scrollableFilters = intersection(FILTERS_TO_RESET_SCROLL, changedFiltersKeys);
      const scrollableFiltersWithUpdatedValues = scrollableFilters.filter(
        (filter) => searchFilters[filter] !== changedFilters[filter]
      );
      const shouldResetScroll = scrollableFiltersWithUpdatedValues.length !== 0;

      if (shouldResetScroll) {
        onScrollableFilterChange();
      }

      updateFiltersAndFireSearchOnChange(changedFilters);
    },
    [searchFilters, onScrollableFilterChange, updateFiltersAndFireSearchOnChange]
  );

  const handleChangeDateRange = (dateRange): void => {
    if (dateRange) {
      handleChange({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
    }
  };

  const isPriceTransparencyFull = getIsPriceTransparencyFull(priceTransparencyLevel);
  const isPriceTransparency = getIsPriceTransparency(priceTransparencyLevel);
  const isBhRepeatBookingFlagEnabled = useGateValue('rollout_bright_horizons_recurring');
  const {
    showDateRange,
    showDaytimeAvailability,
    showDaytimeServiceScheduler,
    showFulltimeAvailability,
    showPets,
    showPetSize,
    showPetType,
    showPrice,
    showStarSitterOnlyFilter,
    lockPriceToggle,
  } = shouldShowControls({
    hasPets,
    isCatOnly: isCatOnly || isCatOnlyBrand,
    serviceOptions: serviceTypeFilterOptions,
    serviceType: serviceType || undefined,
    serviceTypeSlug: searchFilters.serviceType,
    showStarSitterFilter,
    showLowBookingFilter,
    isPriceTransparencyFull,
    isIncludingFees,
    isStaffOrImpersonating,
    searchFilters,
    countryCode,
    isBHUser,
    isBhRepeatBookingFlagEnabled,
  });

  const maxDate = getMaxRequestDate();

  useEffect(() => {
    toggleIsShowingPriceInCurrency(lockPriceToggle);
  }, [lockPriceToggle, toggleIsShowingPriceInCurrency]);

  const availablePets = useAvailablePets(searchFilters.serviceType);

  return (
    <Box>
      <StyledCollectionWrapper>
        <Heading webProps={{ as: 'h2', a11yHidden: true }}>
          <Trans>Search Filters</Trans>
        </Heading>

        {showPets && (
          <FilterWrapper>
            <Pets
              label={plural(
                serviceTypeFilterOptions.pets ? serviceTypeFilterOptions.pets.choices.length : 0,
                {
                  one: 'Your pet:',
                  other: 'Your pets:',
                }
              )}
              onChange={({ pet, petType = [] }) => {
                const localIsCatOnly = getIsCatOnlyPetType(petType);
                const newServiceType = getServiceType(
                  localIsCatOnly,
                  searchFilters.serviceType as ServiceTypeSlug,
                  get(serviceType, 'suggestedPetType')
                );
                return handleChange({
                  pet,
                  petType,
                  catCare: localIsCatOnly,
                  serviceType: newServiceType,
                });
              }}
              petOptions={petOptions}
              searchFilters={searchFilters}
            />
            {!searchFilters.pet.length && (
              <Text size="100" textColor="tertiary">
                {t`Select at least one pet to ensure a more accurate search`}
              </Text>
            )}
          </FilterWrapper>
        )}

        {showPetType && (
          <FilterWrapper>
            <PetCounter
              dogCount={searchFilters.dogCount}
              catCount={searchFilters.catCount}
              puppyCount={searchFilters.puppyCount}
              availablePets={availablePets}
              inputLabelSize="100"
              inputLabelColor="secondary"
              inputLabelMarginBottom="2x"
              triggeredFrom="Search"
              onChange={({ petType, dogCount, catCount, puppyCount }) => {
                const localIsCatOnly = getIsCatOnlyPetType(petType);
                const updatedServiceType = getServiceType(
                  localIsCatOnly,
                  searchFilters.serviceType as ServiceTypeSlug,
                  get(serviceType, 'suggestedPetType')
                );
                const args = {
                  petType,
                  catCare: localIsCatOnly,
                  serviceType: updatedServiceType,
                  dogCount,
                  catCount,
                  puppyCount,
                };
                return handleChange(args);
              }}
              hideHelperText
            />
          </FilterWrapper>
        )}

        <FilterWrapper>
          <ServiceTypeFilter
            filterType={ServiceFilterType.ICON_DROPDOWN}
            label={t`Service type`}
            onChange={(newValues) => {
              handleChange(newValues);
              if (onUpdateInnerHeight) {
                onUpdateInnerHeight();
              }
            }}
            priceFilterMaxValue={priceFilterMaxValue}
            priceFilterMinValue={priceFilterMinValue}
            searchFilters={searchFilters}
            serviceTypeOptions={serviceTypeOptions}
          />
        </FilterWrapper>

        {serviceType && serviceType.displayName && (
          <FilterWrapper>
            <Location
              locationInputId="location-input-sidebar"
              label={i18n._(LOCATION_DEFAULT_TITLE_1)}
              onChange={handleChange}
              onClear={() => handleChange(EMPTY_LOCATION)}
              location={searchFilters.location}
            />
          </FilterWrapper>
        )}

        {showDateRange && (
          <FilterWrapper>
            <OvernightDateRange
              calendarDirection={CalendarDirection.DOWN}
              endPlaceholder={endPlaceholder}
              label={t`Dates`}
              language={language}
              maxDate={maxDate}
              onChange={handleChangeDateRange}
              searchFilters={searchFilters}
              showArrow
              startPlaceholder={startPlaceholder}
            />
          </FilterWrapper>
        )}

        {showDaytimeServiceScheduler && (
          <FilterWrapper>
            <DaytimeServiceScheduler
              dayOfWeekLabel={t`For which days?`}
              displayAs="compact"
              endPlaceholder={endPlaceholder}
              language={language}
              maxDate={maxDate}
              onChange={(frequency) => {
                handleChange({ frequency });
                onUpdateInnerHeight();
              }}
              onDateRangeChange={handleChangeDateRange}
              oneTimeLabel={t`Dates`}
              onSelectedDaysChange={(selectedDays) =>
                handleChange({
                  selectedDays,
                  monday: false,
                  tuesday: false,
                  wednesday: false,
                  thursday: false,
                  friday: false,
                  saturday: false,
                  sunday: false,
                })
              }
              onStartDateChange={handleChangeDateRange}
              repeatStartDateLabel={t`Start Date`}
              searchFilters={searchFilters}
              serviceType={serviceType}
              showArrow
              showLabel
              startPlaceholder={startPlaceholder}
            />
          </FilterWrapper>
        )}

        {showDaytimeAvailability && (
          <FilterWrapper>
            <DayTimeAvailability
              availabilityBuckets={{
                morning: morningAvailability ? morningAvailability.label : null,
                midday: middayAvailability ? middayAvailability.label : null,
                evening: eveningAvailability ? eveningAvailability.label : null,
              }}
              label={t`Which time(s) do you need?`}
              labelIsVisible
              onChange={(availability) => handleChange(availability)}
              searchFilters={searchFilters}
            />
          </FilterWrapper>
        )}

        {showFulltimeAvailability && (
          <FilterWrapper>
            <FulltimeAvailability
              label={t`Sitter is home full-time`}
              onChange={(fulltimeAvailability) => handleChange({ fulltimeAvailability })}
              searchFilters={searchFilters}
            />
          </FilterWrapper>
        )}

        {showPetSize && (
          <FilterWrapper>
            <PetSize
              displayAsIconButtons={false}
              label={t`Dog size (${weightUnit})`}
              onChange={(sizeKey) => handleChange({ [sizeKey]: !searchFilters[sizeKey] })}
              petSizeBuckets={petSizeBuckets}
              searchFilters={searchFilters}
              weightUnit={weightUnit}
            />
          </FilterWrapper>
        )}

        {showPrice && (
          <FilterWrapper>
            <PriceFilter
              currencyCode={currencyCode}
              priceUnit={getPriceUnit(searchFilters.serviceType)}
              priceTransparencyLevel={priceTransparencyLevel}
              isStaffOrImpersonating={isStaffOrImpersonating}
              language={language}
              onChange={({ min, max }) => handleChange({ minprice: min, maxprice: max })}
              selectedRangeMin={searchFilters.minprice}
              selectedRangeMax={searchFilters.maxprice || priceFilterMaxValue}
              sliderMax={priceFilterMaxValue}
              sliderMin={priceFilterMinValue}
              isIncludingFees={isIncludingFees}
            />
          </FilterWrapper>
        )}
      </StyledCollectionWrapper>
      {isBHUser && (
        <Box mb="4x" mt="6x" pl="2x" pr="2x">
          <PriceToggle
            unit={getPriceUnit(searchFilters.serviceType)}
            currencyCode={currencyCode}
            onChange={(toggle) => toggleIsShowingPriceInCurrency(toggle)}
            value={isShowingPriceInCurrency}
            source="search"
            disabled={lockPriceToggle}
          />
        </Box>
      )}
      {showStarSitterOnlyFilter && (
        <Box pb="4x" pt="6x" pl="2x" pr="2x">
          <StarSitterFilter
            label={t`Only show star sitters`}
            onChange={(starSitter) => handleChange({ starSitter })}
            value={searchFilters.starSitter}
            starSitterLink={starSitterLink}
          />
        </Box>
      )}
      {hasAdvancedFilters(serviceTypeFilterOptions) && (
        <StyledCollectionWrapper>
          <AdvancedWrapper>
            <Advanced
              advancedFiltersCount={advancedFiltersCounter(searchFilters, serviceTypeFilterOptions)}
              isPriceTransparency={isPriceTransparency}
              onChange={(filter) => handleChange(filter)}
              searchFiltersOptions={serviceTypeFilterOptions}
              searchFilters={searchFilters}
              onCategoryExpandAndCollapse={onUpdateInnerHeight}
              showLowBookingFilter={!!showLowBookingFilter}
              cardTreatment={cardTreatment}
            />
          </AdvancedWrapper>
        </StyledCollectionWrapper>
      )}
    </Box>
  );
};

export default SidebarFiltersCollection;
